import React from 'react';
import './FraudScoreCard.css';

const FraudScoreCard = ({ fraudScore, taintedAddresses = [] }) => {
  if (!fraudScore) {
    return null;
  }

  const {
    final_score,
    risk_level,
    confidence,
    known_bad_actor_score,
    heuristic_score,
    network_score,
    flags = [],
    reasons = [],
    action,
    recommendations = []
  } = fraudScore;

  // Risk level colors and icons
  const riskConfig = {
    critical: { color: '#dc3545', icon: '🚨', label: 'CRITICAL' },
    high: { color: '#fd7e14', icon: '⚠️', label: 'HIGH' },
    medium: { color: '#ffc107', icon: '⚡', label: 'MEDIUM' },
    low: { color: '#17a2b8', icon: 'ℹ️', label: 'LOW' },
    clean: { color: '#28a745', icon: '✅', label: 'CLEAN' }
  };

  const config = riskConfig[risk_level] || riskConfig.medium;

  // Action colors
  const actionConfig = {
    block: { color: '#dc3545', icon: '🚫', label: 'DO NOT PROCEED' },
    review: { color: '#ffc107', icon: '👁️', label: 'MANUAL REVIEW' },
    proceed: { color: '#28a745', icon: '✅', label: 'PROCEED' }
  };

  const actionInfo = actionConfig[action] || actionConfig.review;

  // Score gauge color
  const getScoreColor = (score) => {
    if (score >= 80) return '#dc3545';
    if (score >= 60) return '#fd7e14';
    if (score >= 40) return '#ffc107';
    if (score >= 20) return '#17a2b8';
    return '#28a745';
  };

  return (
    <div className="fraud-score-card">
      {/* Header */}
      <div className="fraud-header" style={{ borderColor: config.color }}>
        <h3>
          <span className="fraud-icon">{config.icon}</span>
          Fraud Assessment
        </h3>
        <div className="fraud-badge" style={{ backgroundColor: config.color }}>
          {config.label}
        </div>
      </div>

      {/* Main Score */}
      <div className="fraud-main-score">
        <div className="score-gauge">
          <svg width="150" height="150" viewBox="0 0 150 150">
            {/* Background circle */}
            <circle
              cx="75"
              cy="75"
              r="60"
              fill="none"
              stroke="#e9ecef"
              strokeWidth="12"
            />
            {/* Score arc */}
            <circle
              cx="75"
              cy="75"
              r="60"
              fill="none"
              stroke={getScoreColor(final_score)}
              strokeWidth="12"
              strokeDasharray={`${(final_score / 100) * 377} 377`}
              strokeLinecap="round"
              transform="rotate(-90 75 75)"
              style={{ transition: 'stroke-dasharray 0.5s ease' }}
            />
            {/* Score text */}
            <text
              x="75"
              y="70"
              textAnchor="middle"
              fontSize="32"
              fontWeight="bold"
              fill={getScoreColor(final_score)}
            >
              {final_score}
            </text>
            <text
              x="75"
              y="90"
              textAnchor="middle"
              fontSize="14"
              fill="#6c757d"
            >
              / 100
            </text>
          </svg>
        </div>
        <div className="score-details">
          <div className="confidence-bar">
            <label>Confidence: {Math.round(confidence * 100)}%</label>
            <div className="progress">
              <div
                className="progress-bar"
                style={{
                  width: `${confidence * 100}%`,
                  backgroundColor: confidence > 0.7 ? '#28a745' : '#ffc107'
                }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Layer Breakdown */}
      <div className="fraud-layers">
        <h4>Detection Layers</h4>
        <div className="layer-item">
          <div className="layer-header">
            <span className="layer-name">🎯 Known Bad Actors</span>
            <span className="layer-score">{known_bad_actor_score}/100</span>
          </div>
          <div className="layer-bar">
            <div
              className="layer-fill"
              style={{
                width: `${known_bad_actor_score}%`,
                backgroundColor: getScoreColor(known_bad_actor_score)
              }}
            />
          </div>
          {/* Show which bad actors were detected */}
          {taintedAddresses.length > 0 && (
            <div className="layer-details">
              <div className="tainted-addresses-list">
                {taintedAddresses.map((tainted, idx) => (
                  <div key={idx} className="tainted-address-item">
                    <div className="tainted-header">
                      <span className="tainted-icon">
                        {tainted.verified ? '🚨' : '⚠️'}
                      </span>
                      <span className="tainted-description">
                        {tainted.description || tainted.category}
                      </span>
                      {!tainted.verified && (
                        <span className="auto-detected-badge">Auto</span>
                      )}
                    </div>
                    <div className="tainted-details">
                      <span className="tainted-address" title={tainted.address}>
                        {tainted.address.substring(0, 12)}...{tainted.address.substring(tainted.address.length - 6)}
                      </span>
                      <span className={`tainted-severity severity-${tainted.severity}`}>
                        Severity: {tainted.severity}/10
                      </span>
                      {tainted.direction && (
                        <span className="tainted-direction">
                          {tainted.direction === 'input' ? '⬅️ Input' : '➡️ Output'}
                        </span>
                      )}
                    </div>
                    {tainted.tags && tainted.tags.length > 0 && (
                      <div className="tainted-tags">
                        {tainted.tags.slice(0, 3).map((tag, tagIdx) => (
                          <span key={tagIdx} className="tainted-tag">
                            {tag}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="layer-item">
          <div className="layer-header">
            <span className="layer-name">🔍 Heuristic Analysis</span>
            <span className="layer-score">{heuristic_score}/100</span>
          </div>
          <div className="layer-bar">
            <div
              className="layer-fill"
              style={{
                width: `${heuristic_score}%`,
                backgroundColor: getScoreColor(heuristic_score)
              }}
            />
          </div>
        </div>

        {network_score > 0 && (
          <div className="layer-item">
            <div className="layer-header">
              <span className="layer-name">🕸️ Network Analysis</span>
              <span className="layer-score">{network_score}/100</span>
            </div>
            <div className="layer-bar">
              <div
                className="layer-fill"
                style={{
                  width: `${network_score}%`,
                  backgroundColor: getScoreColor(network_score)
                }}
              />
            </div>
          </div>
        )}
      </div>

      {/* Flags */}
      {flags.length > 0 && (
        <div className="fraud-flags">
          <h4>Detection Flags</h4>
          <div className="flag-list">
            {flags.map((flag, idx) => (
              <span key={idx} className="flag-badge">
                {flag.replace(/_/g, ' ')}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Reasons */}
      {reasons.length > 0 && (
        <div className="fraud-reasons">
          <h4>Findings</h4>
          <ul>
            {reasons.map((reason, idx) => (
              <li key={idx}>{reason}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Action & Recommendations */}
      <div className="fraud-action" style={{ borderColor: actionInfo.color }}>
        <div className="action-header" style={{ backgroundColor: actionInfo.color }}>
          <span className="action-icon">{actionInfo.icon}</span>
          <span className="action-label">{actionInfo.label}</span>
        </div>
        {recommendations.length > 0 && (
          <div className="recommendations">
            <ul>
              {recommendations.map((rec, idx) => (
                <li key={idx}>{rec}</li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
};

export default FraudScoreCard;
